/**
 * useUserBot — effective AI bot identity for the current user.
 *
 * Resolves bot name + picture in the order user override -> company
 * default -> hardcoded "Aira" fallback (the resolution lives in the
 * worker; this hook just consumes /ai/user/bot-config). Cached at
 * module level so a re-mount of the bot panel doesn't re-fetch.
 *
 * Components that need to update the identity (the customizer dialog)
 * should call `refresh()` after PUT/DELETE so every consumer
 * re-reads in the same render.
 */
import { useEffect, useState, useCallback } from 'react';
import apiClient from '../services/api/config';

export interface UserBot {
  botName: string;
  botPicture: string | null;
  source: 'user' | 'company' | 'default';
  companyDefault: { botName: string; botPicture: string | null };
  userOverride: { botName: string | null; botPicture: string | null };
}

const DEFAULT_BOT: UserBot = {
  botName: 'Aira',
  botPicture: null,
  source: 'default',
  companyDefault: { botName: 'Aira', botPicture: null },
  userOverride:   { botName: null,   botPicture: null },
};

// Single-flight cache + subscriber set so every consumer of the hook
// re-renders on refresh() without each holding its own copy.
let cached: UserBot | null = null;
let inflight: Promise<UserBot> | null = null;
const subscribers = new Set<(b: UserBot) => void>();

async function fetchBot(): Promise<UserBot> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await apiClient.get('/ai/user/bot-config');
      const data = (r.data?.data || DEFAULT_BOT) as UserBot;
      cached = data;
      subscribers.forEach(fn => fn(data));
      return data;
    } catch {
      cached = DEFAULT_BOT;
      subscribers.forEach(fn => fn(DEFAULT_BOT));
      return DEFAULT_BOT;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useUserBot(): UserBot & { refresh: () => Promise<void> } {
  const [bot, setBot] = useState<UserBot>(cached || DEFAULT_BOT);

  useEffect(() => {
    subscribers.add(setBot);
    if (!cached) fetchBot();
    else setBot(cached);
    return () => { subscribers.delete(setBot); };
  }, []);

  const refresh = useCallback(async () => {
    cached = null;
    await fetchBot();
  }, []);

  return { ...bot, refresh };
}

export default useUserBot;
